'use client';
import React, { useState } from 'react';
import { X, ShieldCheck, CheckCircle, Bitcoin, AlertCircle, Loader2, Copy, Check } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  badgeType: 'seller' | 'buyer';
  onClose: () => void;
  onSuccess: (badgeType: 'seller' | 'buyer') => void;
}

type Step = 'info' | 'payment' | 'crypto' | 'success';
type PaymentMethod = 'paypal' | 'usdt';

const BADGE_AMOUNT_USD = '5.00';
const BADGE_AMOUNT_USDT = '5.00';
const USDT_WALLET = process.env.NEXT_PUBLIC_USDT_WALLET_ADDRESS || '0x9C9bFB619eAdC54b5a3dD707fA5Af93D06cff6f2';

const paypalClientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID || 'sb';

export default function BadgePurchaseModal({ badgeType, onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('info');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('paypal');
  const [txHash, setTxHash] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [walletCopied, setWalletCopied] = useState(false);

  const isSeller = badgeType === 'seller';
  const badgeLabel = isSeller ? 'بائع موثق' : 'مشتري موثق';
  const badgeColor = isSeller ? 'text-blue-600' : 'text-emerald-600';
  const badgeBg = isSeller ? 'bg-blue-50' : 'bg-emerald-50';
  const badgeBorder = isSeller ? 'border-blue-200' : 'border-emerald-200';
  const badgeIcon = isSeller ? '🏠' : '🔑';

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(USDT_WALLET);
    setWalletCopied(true);
    setTimeout(() => setWalletCopied(false), 2000);
  };

  const grantBadge = async (paymentId?: string) => {
    const supabase = createClient();
    const column = isSeller ? 'seller_badge' : 'buyer_badge';
    const paidAtColumn = isSeller ? 'seller_badge_paid_at' : 'buyer_badge_paid_at';

    if (user?.id) {
      await supabase
        .from('user_profiles')
        .update({ [column]: true, [paidAtColumn]: new Date().toISOString() })
        .eq('id', user.id);

      await supabase.from('badge_payments').insert({
        user_id: user.id,
        badge_type: badgeType,
        payment_method: paymentId ? 'paypal' : 'usdt',
        payment_id: paymentId || txHash,
        amount_usd: parseFloat(BADGE_AMOUNT_USD),
        status: 'confirmed',
        confirmed_at: new Date().toISOString(),
      });
    }

    setStep('success');
    onSuccess(badgeType);
  };

  const handleVerifyTx = async () => {
    if (!txHash.trim()) {
      setVerifyError('يرجى إدخال رقم التحويل');
      return;
    }
    setVerifying(true);
    setVerifyError('');
    try {
      const res = await fetch('/api/crypto/verify-tx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ txHash: txHash.trim(), propertyId: `badge_${badgeType}_${user?.id}` }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await grantBadge();
        toast.success('تم التحقق! تم منح الشارة بنجاح');
      } else {
        setVerifyError(data.error || 'فشل التحقق من المعاملة');
      }
    } catch {
      setVerifyError('خطأ في الاتصال بالخادم');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className={badgeColor} />
            <span className="font-600 text-gray-900 text-sm">
              {step === 'success' ? 'تم الحصول على الشارة!' : `شراء شارة ${badgeLabel}`}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Step: Info */}
        {step === 'info' && (
          <div className="p-5">
            <div className={`rounded-2xl p-5 mb-5 border ${badgeBg} ${badgeBorder} text-center`}>
              <div className="text-4xl mb-2">{badgeIcon}</div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <ShieldCheck size={20} className={badgeColor} />
                <span className={`font-700 text-lg ${badgeColor}`}>{badgeLabel}</span>
              </div>
              <p className="text-sm text-gray-600">
                {isSeller
                  ? 'شارة تُثبت أنك بائع موثق — تظهر على إعلاناتك وتزيد الثقة' :'شارة تُثبت أنك مشتري موثق — تُظهر جديتك للبائعين'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">رسوم الشارة (مرة واحدة)</span>
              <span className="text-xl font-800 text-gray-900">$5.00 USD</span>
            </div>

            <ul className="space-y-2 mb-5">
              {(isSeller ? [
                'شارة ✓ موثق تظهر على جميع إعلاناتك',
                'زيادة الثقة مع المشترين والمستأجرين',
                'أولوية في نتائج البحث',
              ] : [
                'شارة ✓ مشتري موثق على ملفك الشخصي',
                'إثبات الجدية للبائعين',
                'وصول أسرع لبيانات التواصل',
              ]).map((item, i) => (
                <li key={`feat-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-xs text-gray-500 mb-2 font-500">اختر طريقة الدفع:</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                  paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <svg viewBox="0 0 101 32" className="h-5" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#003087" d="M12.237 2.8H6.437c-.4 0-.7.3-.8.7L3.237 18.6c0 .3.2.5.5.5h2.8c.4 0 .7-.3.8-.7l.6-3.8c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.7-4.5-1.7zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#003087" d="M35.437 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#003087" d="M49.737 8.2h-2.8c-.3 0-.5.1-.6.4l-3.7 5.4-1.6-5.2c-.1-.3-.4-.6-.8-.6h-2.8c-.3 0-.5.3-.4.6l2.9 8.6-2.7 3.9c-.2.3 0 .7.3.7h2.8c.3 0 .5-.1.6-.4l8.8-12.7c.2-.3 0-.7-.3-.7z"/>
                  <path fill="#009cde" d="M58.637 2.8h-5.8c-.4 0-.7.3-.8.7l-2.4 15.1c0 .3.2.5.5.5h3c.3 0 .5-.2.5-.5l.7-4.2c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.5-4.5-1.5zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#009cde" d="M81.837 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#009cde" d="M86.337 3.1l-2.4 15.4c0 .3.2.5.5.5h2.4c.4 0 .7-.3.8-.7l2.4-15.1c0-.3-.2-.5-.5-.5h-2.7c-.3 0-.5.2-.5.4z"/>
                </svg>
                <span className="text-xs font-600 text-gray-700">PayPal</span>
              </button>
              <button
                onClick={() => setPaymentMethod('usdt')}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                  paymentMethod === 'usdt' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-1">
                  <Bitcoin size={16} className="text-emerald-600" />
                  <span className="text-sm font-800 text-emerald-700">USDT</span>
                </div>
                <span className="text-xs font-600 text-gray-700">كريبتو BEP20</span>
              </button>
            </div>

            <button
              onClick={() => setStep(paymentMethod === 'usdt' ? 'crypto' : 'payment')}
              className={`w-full py-3 font-600 rounded-xl transition-all duration-150 active:scale-95 text-white ${
                isSeller ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              {paymentMethod === 'usdt' ? `الدفع بـ USDT — ${BADGE_AMOUNT_USDT} USDT` : `المتابعة للدفع — $${BADGE_AMOUNT_USD}`}
            </button>
            <button onClick={onClose} className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              إلغاء
            </button>
          </div>
        )}

        {/* Step: PayPal */}
        {step === 'payment' && (
          <div className="p-5">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 mb-1">ادفع بأمان عبر PayPal</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">شارة {badgeLabel}</span>
              <span className="font-800 text-gray-900 tabular-nums">${BADGE_AMOUNT_USD} USD</span>
            </div>
            <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD', intent: 'capture' }}>
              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                createOrder={async () => {
                  const res = await fetch('/api/paypal/create-order', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ amount: BADGE_AMOUNT_USD, description: `شارة ${badgeLabel} - Directly` }),
                  });
                  const data = await res.json();
                  if (!res.ok || !data.id) {
                    toast.error('فشل إنشاء طلب الدفع');
                    throw new Error(data.error || 'create-order failed');
                  }
                  return data.id;
                }}
                onApprove={async (data) => {
                  const res = await fetch('/api/paypal/capture-order', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderID: data.orderID }),
                  });
                  const captureData = await res.json();
                  if (!res.ok) {
                    toast.error('فشل تأكيد الدفع');
                    return;
                  }
                  if (captureData?.status === 'COMPLETED') {
                    await grantBadge(data.orderID);
                    toast.success('تم الدفع بنجاح! تم منح الشارة');
                  } else {
                    toast.error('لم يكتمل الدفع');
                  }
                }}
                onError={() => toast.error('حدث خطأ أثناء الدفع')}
                onCancel={() => toast('تم إلغاء عملية الدفع')}
              />
            </PayPalScriptProvider>
            <button onClick={() => setStep('info')} className="w-full py-2 mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              رجوع
            </button>
          </div>
        )}

        {/* Step: USDT */}
        {step === 'crypto' && (
          <div className="p-5">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-2">
                <Bitcoin size={22} className="text-emerald-600" />
              </div>
              <h3 className="font-700 text-gray-900 text-sm mb-0.5">الدفع بـ USDT (BEP20 — BSC)</h3>
              <p className="text-xs text-gray-500">أرسل المبلغ ثم أدخل رقم التحويل</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 mb-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">المبلغ المطلوب</span>
              <span className="font-800 text-emerald-700 tabular-nums">{BADGE_AMOUNT_USDT} USDT</span>
            </div>
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1.5 font-500">عنوان المحفظة (BEP20):</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                <span className="flex-1 text-xs font-mono text-gray-800 break-all">{USDT_WALLET}</span>
                <button onClick={handleCopyWallet} className="flex-shrink-0 p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">
                  {walletCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} className="text-gray-600" />}
                </button>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">تأكد من إرسال USDT على شبكة <strong>BEP20</strong> فقط.</p>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-600 font-500 block mb-1.5">رقم التحويل (Transaction Hash):</label>
              <input
                type="text"
                value={txHash}
                onChange={(e) => { setTxHash(e.target.value); setVerifyError(''); }}
                placeholder="الصق رقم التحويل هنا..."
                className="w-full px-3 py-2.5 text-xs font-mono border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white placeholder-gray-400"
                dir="ltr"
              />
              {verifyError && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600">{verifyError}</p>
                </div>
              )}
            </div>
            <button
              onClick={handleVerifyTx}
              disabled={verifying || !txHash.trim()}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-600 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {verifying ? <><Loader2 size={16} className="animate-spin" />جاري التحقق...</> : 'تحقق ومنح الشارة'}
            </button>
            <button onClick={() => setStep('info')} className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">رجوع</button>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="p-5 text-center">
            <div className={`w-16 h-16 ${badgeBg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
              <ShieldCheck size={32} className={badgeColor} />
            </div>
            <h3 className="font-700 text-gray-900 text-lg mb-2">تهانينا! 🎉</h3>
            <p className="text-sm text-gray-600 mb-1">تم منح شارة <strong>{badgeLabel}</strong> لحسابك</p>
            <p className="text-xs text-gray-400 mb-6">ستظهر الشارة على ملفك الشخصي وإعلاناتك</p>
            <button
              onClick={onClose}
              className={`w-full py-3 font-600 rounded-xl text-white transition-colors ${
                isSeller ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              رائع، شكراً!
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
